import React, { useState } from 'react';
import { Box, Typography, Chip, Button, CircularProgress } from '@mui/material';
// Note: ChevronRightIcon and ExpandMoreIcon kept for waiting state UI
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PendingIcon from '@mui/icons-material/Pending';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import VisibilityIcon from '@mui/icons-material/Visibility';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useMutation } from '@apollo/client';
import { PlanStep, Participant, RequestThreadStatus } from '../../types/generated/graphql';
import { DELETE_STEP_REQUEST_THREAD, GET_PLAN } from '../../graphql/queries';
import { colors } from '../../theme/colors';
import GravatarAvatar from '../GravatarAvatar';
import AssignOrRequestSheet, { DelegatedRequestThreadDraft } from './AssignOrRequestSheet';

interface DelegateTaskControlProps {
  planId: string;
  step: PlanStep;
  participants?: Participant[];
  /** When true, shows only the status (for WAITING mode) */
  showStatusOnly?: boolean;
  /** Callback when delegation thread is created */
  onDelegationCreated?: (draft: DelegatedRequestThreadDraft) => void;
}

// Get status display info
const getStatusInfo = (status: RequestThreadStatus) => {
  switch (status) {
    case RequestThreadStatus.Satisfied:
      return { label: 'Completed', color: 'success' as const, Icon: DoneAllIcon, colorHex: colors.green[600] };
    case RequestThreadStatus.Ready:
      return { label: 'Ready', color: 'info' as const, Icon: CheckCircleIcon, colorHex: colors.blue[600] };
    case RequestThreadStatus.Open:
      return { label: 'Pending', color: 'warning' as const, Icon: PendingIcon, colorHex: colors.amber[600] };
    default:
      return { label: status, color: 'default' as const, Icon: PendingIcon, colorHex: colors.gray[500] };
  }
};

/**
 * Delegate Task Control
 *
 * A simple control for step delegation that opens AssignOrRequestSheet directly.
 *
 * Features:
 * - Single click opens the delegation sheet immediately
 * - Proper waiting state when active request thread exists
 * - View/Nudge/Cancel actions for active threads
 */
export const DelegateTaskControl: React.FC<DelegateTaskControlProps> = ({
  planId,
  step,
  participants = [],
  showStatusOnly = false,
  onDelegationCreated,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [deletingThreadId, setDeletingThreadId] = useState<string | null>(null);

  // Get existing active thread
  const requestThreads = step.requestThreads || [];
  const activeThread = requestThreads.find(
    (t) => t.status === RequestThreadStatus.Open || t.status === RequestThreadStatus.Ready
  );

  // Find assignee participant for active thread
  const assignee = participants.find((p) => p.id === step.assignedToId);

  // Mutation to delete thread
  const [deleteThread, { loading: deleting }] = useMutation(DELETE_STEP_REQUEST_THREAD, {
    refetchQueries: [{ query: GET_PLAN, variables: { id: planId } }],
    awaitRefetchQueries: true,
    onCompleted: () => setDeletingThreadId(null),
    onError: () => setDeletingThreadId(null),
  });

  const handleOpenSheet = () => {
    setSheetOpen(true);
  };

  const handleCloseSheet = () => {
    setSheetOpen(false);
  };

  const handleDelegationCreated = (draft: DelegatedRequestThreadDraft) => {
    onDelegationCreated?.(draft);
  };

  const handleCancelDelegation = async (threadId: string) => {
    if (!window.confirm('Cancel this delegation?')) return;

    setDeletingThreadId(threadId);
    try {
      await deleteThread({
        variables: { planId, stepId: step.id, threadId },
      });
    } catch (err) {
      console.error('Failed to cancel delegation:', err);
    }
  };

  // WAITING STATE - Active thread exists
  if (activeThread) {
    const statusInfo = getStatusInfo(activeThread.status);
    const StatusIcon = statusInfo.Icon;
    const isDeleting = deletingThreadId === activeThread.threadId;
    const delegateName = assignee?.name || 'delegate';

    // COLLAPSED WAITING VIEW
    if (!isExpanded) {
      return (
        <Box
          onClick={() => setIsExpanded(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 1,
            px: 2,
            cursor: 'pointer',
            borderRadius: 1,
            '&:hover': { bgcolor: colors.gray[50] },
          }}
        >
          <PersonAddIcon sx={{ fontSize: 16, color: colors.amber[600] }} />
          <Typography variant="body2" sx={{ color: colors.gray[500] }}>
            Delegation
          </Typography>
          <ChevronRightIcon sx={{ fontSize: 16, color: colors.gray[400] }} />
          {assignee && (
            <GravatarAvatar
              email={assignee.email}
              name={assignee.name}
              size={20}
              showTooltip={false}
            />
          )}
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Waiting on {delegateName}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Chip
            icon={<StatusIcon sx={{ fontSize: 12 }} />}
            label={statusInfo.label}
            size="small"
            color={statusInfo.color}
            sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.7rem', px: 0.75 } }}
          />
        </Box>
      );
    }

    // EXPANDED WAITING VIEW
    return (
      <Box
        sx={{
          borderLeft: `3px solid ${colors.amber[400]}`,
          pl: 2,
          py: 1.5,
          mx: 2,
        }}
      >
        {/* Collapse header */}
        <Box
          onClick={() => setIsExpanded(false)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            mb: 2,
          }}
        >
          <ExpandMoreIcon sx={{ fontSize: 18, color: colors.gray[500] }} />
          <Typography variant="body2" sx={{ color: colors.gray[500], fontWeight: 500 }}>
            Delegation
          </Typography>
        </Box>

        {/* Delegate info card */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            bgcolor: colors.amber[50],
            borderRadius: 1.5,
            border: `1px solid ${colors.amber[200]}`,
            mb: 2,
          }}
        >
          {assignee ? (
            <GravatarAvatar
              email={assignee.email}
              name={assignee.name}
              size={36}
            />
          ) : (
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: colors.amber[200],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PersonAddIcon sx={{ fontSize: 18, color: colors.amber[700] }} />
            </Box>
          )}

          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {assignee?.name || 'Delegate'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {assignee?.email || 'Waiting for response...'}
            </Typography>
          </Box>

          <Chip
            icon={<StatusIcon sx={{ fontSize: 12 }} />}
            label={statusInfo.label}
            size="small"
            sx={{
              height: 22,
              bgcolor: `${statusInfo.colorHex}15`,
              color: statusInfo.colorHex,
              fontWeight: 600,
              '& .MuiChip-icon': { color: statusInfo.colorHex },
            }}
          />
        </Box>

        {/* Sent time */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mb: 2,
            color: colors.textSecondary,
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption">
            Sent {activeThread.createdAt ? new Date(activeThread.createdAt).toLocaleDateString() : 'recently'}
          </Typography>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="text"
            size="small"
            startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
            sx={{
              color: colors.gray[600],
              fontSize: '0.75rem',
              textTransform: 'none',
              '&:hover': { bgcolor: colors.gray[100] },
            }}
          >
            View
          </Button>
          <Button
            variant="text"
            size="small"
            startIcon={<NotificationsIcon sx={{ fontSize: 14 }} />}
            sx={{
              color: colors.gray[600],
              fontSize: '0.75rem',
              textTransform: 'none',
              '&:hover': { bgcolor: colors.gray[100] },
            }}
          >
            Nudge
          </Button>
          <Button
            variant="text"
            size="small"
            startIcon={
              isDeleting ? (
                <CircularProgress size={12} sx={{ color: colors.red[600] }} />
              ) : (
                <CancelIcon sx={{ fontSize: 14 }} />
              )
            }
            onClick={() => handleCancelDelegation(activeThread.threadId)}
            disabled={isDeleting || deleting}
            sx={{
              color: colors.red[600],
              fontSize: '0.75rem',
              textTransform: 'none',
              '&:hover': { bgcolor: colors.red[50] },
            }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    );
  }

  // INPUT MODE - No active thread, show simple entry point that opens sheet directly
  return (
    <>
      <Box
        onClick={handleOpenSheet}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1,
          px: 2,
          cursor: 'pointer',
          borderRadius: 1,
          transition: 'background-color 0.15s',
          '&:hover': { bgcolor: colors.amber[50] },
        }}
      >
        <PersonAddIcon sx={{ fontSize: 16, color: colors.amber[600] }} />
        <Typography variant="body2" sx={{ color: colors.amber[700], fontWeight: 500 }}>
          Delegate task
        </Typography>
        <ChevronRightIcon sx={{ fontSize: 16, color: colors.amber[400] }} />
      </Box>

      {/* Assignment/Request Sheet */}
      <AssignOrRequestSheet
        open={sheetOpen}
        onClose={handleCloseSheet}
        planId={planId}
        mode="delegate"
        step={step}
        participants={participants}
        onComplete={handleCloseSheet}
        onDelegationCreated={handleDelegationCreated}
      />
    </>
  );
};

export default DelegateTaskControl;
